import { defineComponent, h, ref, computed, VNode } from 'vue';
import { box, col, cols } from '@/ui/components';
import { issueEdit } from '@/ui/components/issue/IssueEdit';
import { issueLogs } from '@/ui/pages/issue/IssueLogs';
import { IssueRecordEditable } from '@/ui/pages/issue/issueTypes';
import { VNodeData } from 'vue/types/vnode.d';
import { IssueType } from '@/entities/issue';
import {
  informationBoxSeeReport,
} from '@/ui/pages/issue/issueEditWithLog/InformationBoxSeeReport';
import { IssueRecord } from '@/entities/issue/v2';
import { InformationBoxProps } from '@/ui/pages/issue/issueEditWithLog/types';
import { informationBoxSeeDetails } from '@/ui/pages/issue/issueEditWithLog/InformationBoxSeeDetails';
import ApproveForm from '@/ui/components/approval/ApproveForm.vue';
import { ApprovalStatus } from '@/entities/approvals/constants';
import { approvalsEnabledForOrganization, hasSomePermission } from '@/ui/auth/auth';
import { actionApprovalsPermissions } from '@/ui/auth/auth.types';

interface IssueEditProps {
  issueId: number;
  hideSendButton?: boolean;
}

type InformationBoxReturnType = ((data: VNodeData & { props: InformationBoxProps }) => VNode) | undefined;

const informationBox: Record<IssueType, InformationBoxReturnType> = {
  [IssueType.ManualForm]: informationBoxSeeReport,
  [IssueType.InForm]: informationBoxSeeDetails,
  [IssueType.AdHoc]: undefined,
  [IssueType.Automatic]: informationBoxSeeDetails,
  [IssueType.Sensor]: undefined,
  [IssueType.AutomaticForm]: informationBoxSeeReport,
};

export const IssueEditWithLogs = defineComponent<IssueEditProps>({
  name: 'IssueEditWithLogs',
  props: ['issueId', 'hideSendButton'],
  setup(props, context) {
    const issue = ref<IssueRecord | null>(null);

    const onIssueEdited = (i: IssueRecordEditable) => {
      context.emit('edit:issue', i);
    };

    const onIssueFetch = (i: IssueRecord) => {
      issue.value = i;
      context.emit('fetch:issue', i);
    };

    const onApprovalUpdated = (status: string) => {
      if (issue?.value?.data?.approval) {
        issue.value.data.approval.status = status;
      }
    };

    const issueBox = computed(() => (
      issue.value ? informationBox[issue.value.data.type!] : null
    ));

    const isApprovalFormVisible = computed(() => (
      issue.value && issue.value?.data.approval?.status === ApprovalStatus.Pending
    ));

    const canSeeApprovals = () => {
      return approvalsEnabledForOrganization.value
        && hasSomePermission(actionApprovalsPermissions);
    };

    return function render() {
      return cols([
        col([
          issue.value && issueBox.value
            ? issueBox.value({ props: { issue: issue.value } })
            : null,
          box([
            issueEdit({
              id: props.issueId,
              hideSubmitButton: props.hideSendButton,
            }, {
              edited: onIssueEdited,
              'issue:fetch': onIssueFetch,
            }),
          ]),
        ]),
        col([
          isApprovalFormVisible.value && canSeeApprovals() && h(ApproveForm, {
            props: {
              inspectionId: null,
              issueId: props.issueId,
              approvalId: issue.value?.data?.approval?.id,
            },
            on: {
              updated: onApprovalUpdated,
            },
          }),
          issueLogs({
            props: {
              issueId: props.issueId,
              approvalId: issue.value?.data?.approval?.id,
              approvalStatus: issue.value?.data?.approval?.status,
            },
          }),
        ]),
      ], 'issue-with-logs');
    };
  },
});

export function issueEditWithLogs(data: VNodeData & { props: IssueEditProps }) {
  return h(IssueEditWithLogs, data);
}
